import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import { MilestoneDetailModal } from "../components/MilestoneDetailModal";
import { ParcelDrawer, ProceedingDetailModal } from "../components/ParcelsTab";
import { useConnection, useProceeding } from "../hooks";
import type { ControlItem } from "./types";

/**
 * Wejście w pozycję z list doradcy (Kontrola procesu / TODO) otwiera dokładnie ten
 * sam widok, co kliknięcie tego obiektu w jego własnej zakładce: kamień → modal
 * kamienia (jak na tablicy i na grafie), działka → drawer działki, sprawa → podgląd
 * sprawy. Listy trzymają tylko identyfikatory z `ref`, więc projekt / sprawę
 * dociągamy dopiero po kliknięciu.
 */
export type QuickTarget =
  | { kind: "milestone"; connectionId: string; milestoneKey: string }
  | { kind: "parcel"; connectionId: string; parcelId: string }
  | { kind: "proceeding"; proceedingId: string };

/** Na co wskazuje pozycja listy — null, gdy nie da się jej z niczym powiązać. */
export function quickTargetOf(item: ControlItem, connectionId: string | undefined): QuickTarget | null {
  const ref = item.ref ?? {};
  if (!connectionId && !ref.proceeding_id) return null;
  if (ref.milestone_key && connectionId) {
    return { kind: "milestone", connectionId, milestoneKey: ref.milestone_key };
  }
  if (item.kind === "proceeding" && ref.proceeding_id) {
    return { kind: "proceeding", proceedingId: ref.proceeding_id };
  }
  if (ref.parcel_id && connectionId) {
    return { kind: "parcel", connectionId, parcelId: ref.parcel_id };
  }
  if (ref.proceeding_id) return { kind: "proceeding", proceedingId: ref.proceeding_id };
  return null;
}

function LoadingOverlay() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <Spinner />
    </div>
  );
}

/** Pozycji nie da się otworzyć (brak dostępu do projektu / obiekt zniknął) —
 * mówimy to wprost, zamiast zostawiać klik bez reakcji. */
function Unavailable({ what, onClose }: { what: string; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title="Nie można otworzyć">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Nie udało się wczytać: {what}. Pozycja mogła zostać usunięta albo nie masz dostępu do tego projektu.
      </p>
    </Modal>
  );
}

function MilestoneQuick({ connectionId, milestoneKey, onClose }: {
  connectionId: string; milestoneKey: string; onClose: () => void;
}) {
  const { data: connection, isLoading } = useConnection(connectionId);
  const milestone = connection?.milestones.find((m) => m.key === milestoneKey);
  if (isLoading) return <LoadingOverlay />;
  if (!connection || !milestone) return <Unavailable what="kamień milowy" onClose={onClose} />;
  return <MilestoneDetailModal connection={connection} milestone={milestone} onClose={onClose} />;
}

function ParcelQuick({ connectionId, parcelId, onClose }: {
  connectionId: string; parcelId: string; onClose: () => void;
}) {
  const { data: connection, isLoading } = useConnection(connectionId);
  if (isLoading) return <LoadingOverlay />;
  if (!connection) return <Unavailable what="działka" onClose={onClose} />;
  return (
    <ParcelDrawer
      parcelId={parcelId}
      connectionId={connection.id}
      projectId={connection.project}
      operator={connection.operator}
      onClose={onClose}
    />
  );
}

function ProceedingQuick({ proceedingId, onClose }: { proceedingId: string; onClose: () => void }) {
  const { data: proceeding, isLoading } = useProceeding(proceedingId);
  if (isLoading) return <LoadingOverlay />;
  if (!proceeding) return <Unavailable what="sprawa urzędowa" onClose={onClose} />;
  return <ProceedingDetailModal proceeding={proceeding} onClose={onClose} />;
}

export function QuickTargetModal({ target, onClose }: { target: QuickTarget; onClose: () => void }) {
  if (target.kind === "milestone") {
    return (
      <MilestoneQuick connectionId={target.connectionId} milestoneKey={target.milestoneKey} onClose={onClose} />
    );
  }
  if (target.kind === "parcel") {
    return <ParcelQuick connectionId={target.connectionId} parcelId={target.parcelId} onClose={onClose} />;
  }
  return <ProceedingQuick proceedingId={target.proceedingId} onClose={onClose} />;
}
